import React, { useEffect, useState } from 'react';
import { calculateAverage, calculatePercentile } from '../utils/Stats';
import { useTranslation } from 'react-i18next';
import MonitorTitle from './MonitorTitle';

function determineMonitorHealth(monitor) {
  const healthSamples = monitor.timeSeriesData.last24Hours.slice(-2);
  const healthLatency = calculatePercentile(healthSamples
    .filter(x => x[1] !== -1)
    .map(x => x[1]), monitor.percentile);
  const healthUptime = calculateAverage(healthSamples
    .filter(x => x[3] > 0)
    .map(x => x[2] / x[3]));

  const result = { uptime: 'OK', latency: 'OK' };

  if (healthUptime >= 0) {
    if (monitor.thresholds.uptime.error > 0
      && healthUptime <= monitor.thresholds.uptime.error) {
      result.uptime = 'ERROR';
    } else if (monitor.thresholds.uptime.warning > 0
      && healthUptime <= monitor.thresholds.uptime.warning) {
      result.uptime = 'WARNING';
    }
  }

  if (healthLatency >= 0) {
    if (monitor.thresholds.latency.error > 0
      && healthLatency >= monitor.thresholds.latency.error) {
      result.latency = 'ERROR';
    } else if (monitor.thresholds.latency.warning > 0
      && healthLatency >= monitor.thresholds.latency.warning) {
      result.latency = 'WARNING';
    }
  }

  return { ...result, currentLatency: healthLatency, currentUptime: healthUptime };
}

const HEALTH_STYLES = {
  OK: {
    pill: 'bg-secondary text-secondary-dark',
    dot: 'bg-secondary-dark',
  },
  WARNING: {
    pill: 'bg-primary/30 text-yellow-700',
    dot: 'bg-primary-dark',
  },
  ERROR: {
    pill: 'bg-red-50 text-red-700',
    dot: 'bg-red-500',
  },
  DISABLED: {
    pill: 'bg-gray-100 text-neutral',
    dot: 'bg-neutral',
  },
};

function HealthPill({ health, label }) {
  const style = HEALTH_STYLES[health] || HEALTH_STYLES.DISABLED;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${style.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {label}
    </span>
  );
}

function ChevronIcon({ isOpen }) {
  return (
    <svg
      className={`w-5 h-5 text-neutral transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
      fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function getHealthLabel(health, t) {
  if (health.uptime === 'OK' && health.latency === 'OK') {
    return { level: 'OK', label: t('status.healthy') };
  }
  if (health.uptime === 'ERROR' || health.latency === 'ERROR') {
    const labels = [];
    if (health.uptime === 'ERROR') labels.push(t('status.availabilityError'));
    if (health.latency === 'ERROR') labels.push(t('status.latencyError'));
    return { level: 'ERROR', label: labels.join(' · ') };
  }
  const labels = [];
  if (health.uptime === 'WARNING') labels.push(t('status.availabilityWarning'));
  if (health.latency === 'WARNING') labels.push(t('status.latencyWarning'));
  return { level: 'WARNING', label: labels.join(' · ') };
}

export default function MonitorSummary({ monitor, timespan }) {
  const { t } = useTranslation();
  const [health, setHealth] = useState();
  const [isOpen, setIsOpen] = useState(true); // set default to true

  useEffect(() => {
    setHealth(determineMonitorHealth(monitor));
  }, [monitor, timespan]);

  if (!health) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5 animate-pulse">
        <div className="h-5 bg-gray-100 rounded w-1/3" />
      </div>
    );
  }

  const isEnabled = monitor.enabled;
  const { level, label } = isEnabled
    ? getHealthLabel(health, t)
    : { level: 'DISABLED', label: t('status.disabled') };

  const detailText = [];
  if (isEnabled) {
    if (health.uptime === 'OK' && health.latency === 'OK') {
      detailText.push(t('status.allSystemsRunAsExpected'));
    }
    if (health.latency !== 'OK') {
      detailText.push(t('status.highLatencyText', {
        ms: health.currentLatency.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      }));
    }
    if (health.uptime !== 'OK') {
      detailText.push(t('status.lowAvailabilityText', {
        percent: (health.currentUptime * 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
      }));
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden transition-shadow duration-200 hover:shadow-md">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        onClick={() => isEnabled && setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <MonitorTitle title={monitor.title} className="font-semibold text-base sm:text-lg truncate mr-4" />
        <div className="flex items-center gap-3 flex-shrink-0">
          <HealthPill health={level} label={label} />
          {isEnabled && <ChevronIcon isOpen={isOpen} />}
        </div>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 pb-4 pt-3 text-sm text-neutral border-t border-gray-100">
          {detailText.map((text, i) => (
            <p key={i} className={i > 0 ? 'mt-1' : ''}>{text}</p>
          ))}
        </div>
      </div>
    </div>
  );
}
